import LayoutUtil from '@frontend/utils/LayoutUtil';
import focusLock from 'dom-focus-lock';
import throttle from 'lodash/throttle';

export default class Header {
	private readonly element: HTMLElement;

	public static domSelectors = {
		mainWrapper: '.m-header',
		toggleButton: '.m-header__overlay-toggle',
		closeButton: '.m-header__overlay-close-button',
		searchButton: '.m-header__search-button',
		searchContainer: '.m-header__search',
		searchInput: '.m-search__input',
		backButton: '.m-header__overlay-back-button',
		topMainLink: '.m-header__main-link',
		metaLink: '.m-header__meta-link',
		overlay: '.m-header__overlay',
		overlayLinksWrapper: '.m-header__overlay-links-wrapper',
		overlayLink: '.m-header__overlay-link',
		overlayMetaLink: '.m-header__overlay-meta-link',
		overlayLinkGroup: '.m-header__overlay-link-group',
		linkImage: '.m-header__overlay-image',
		contextualLink: '[data-link-context]'
	};

	public static stateClasses = {
		hasDisplayedOverlay: 'has-displayed-overlay',
		hasOpenOverlay: 'has-open-overlay',
		hasVisibleSearch: 'has-visible-search',
		hasVisibleNavigation: 'has-visible-navigation',
		hasHoveredParentLink: 'has-hovered-parent-link',
		isSelectedLinkImage: 'is-selected-link-image',
		hasHiddenNavigationBar: 'has-hidden-navigation-bar',
		isHidden: 'is-hidden',
		isActive: 'is-active', // for indicators of the current server rendered page
		hasOpenLinkGroup: 'has-open-link-group', // single link which handles a link group
		isOpenLinkGroup: 'is-open-link-group', // for link groups
		isCurrentLinkGroup: 'is-current-link-group' // for current ink groups
	};

	private static attributes = {
		currentLevel: 'data-current-level',
		level: 'data-level',
		parentIds: 'data-parent-ids',
		linkId: 'data-link-id',
		imageId: 'data-image-id',
		hasSubLinks: 'data-has-sub-links',
		hoveredLinkContext: 'data-hovered-link-context'
	};

	private isSearchInitialized: boolean = false;

	public ignoreScrollEvent = false;

	protected lastScrollPosition: number = 0;

	protected upScrollSnapshot: number | null = null;

	protected downScrollSnapshot: number | null = null;

	protected throttledScrollEvent = throttle(this.checkPageScroll, 200);

	public constructor(element: HTMLElement) {
		this.element = element;
		this.init();
	}

	private imageVisibilityTimeout?: number;

	private init(): void {
		const overlayToggle: HTMLElement | null = this.element.querySelector(Header.domSelectors.toggleButton);
		const overlayCloseButton: HTMLElement | null = this.element.querySelector(Header.domSelectors.closeButton);
		const backButton: HTMLElement | null = this.element.querySelector(Header.domSelectors.backButton);
		const searchButton: HTMLElement | null = this.element.querySelector(Header.domSelectors.searchButton);
		const overlayLinks: HTMLElement[] = Array.from(this.element.querySelectorAll(Header.domSelectors.overlayLink));
		const overlayMetaLinks: HTMLElement[] = Array.from(
			this.element.querySelectorAll(Header.domSelectors.overlayMetaLink)
		);
		const contextualLinks: HTMLElement[] = Array.from(
			this.element.querySelectorAll(Header.domSelectors.contextualLink)
		);
		const topMainLinksWithSubLinks: HTMLElement[] = Array.from(
			this.element.querySelectorAll(`${Header.domSelectors.topMainLink}[${Header.attributes.hasSubLinks}]`)
		);
		const metaLinksWithSubLinks: HTMLElement[] = Array.from(
			this.element.querySelectorAll(`${Header.domSelectors.metaLink}[${Header.attributes.hasSubLinks}]`)
		);
		const overlayLinksWithSubLinks: HTMLElement[] = Array.from(
			this.element.querySelectorAll(`${Header.domSelectors.overlayLink}[${Header.attributes.hasSubLinks}]`)
		);
		const overlayMetaLinksWithSubLinks: HTMLElement[] = Array.from(
			this.element.querySelectorAll(`${Header.domSelectors.overlayMetaLink}[${Header.attributes.hasSubLinks}]`)
		);

		if (overlayToggle) {
			overlayToggle.addEventListener('click', (): void => {
				if (this.isOverlayClosed()) {
					this.showNavigation();
					this.openOverlay();
				} else {
					this.closeOverlay();
				}
			});
		}

		if (overlayCloseButton) {
			overlayCloseButton.addEventListener('click', (): void => this.closeOverlay());
		}

		document.addEventListener('keydown', (event: KeyboardEvent): void => {
			if (event.key === 'Escape') {
				this.closeOverlay();
			}
		});

		if (searchButton) {
			searchButton.addEventListener('click', (): void => {
				if (this.isSearchHidden()) {
					if (this.isOverlayClosed()) {
						this.openOverlay();
					}
					this.resetNavigation();
					this.hideNavigation();
					this.showSearch();
				} else {
					this.closeOverlay();
				}
			});
		}

		[...topMainLinksWithSubLinks, ...metaLinksWithSubLinks].forEach((element): void => {
			element.addEventListener('click', (event): void => {
				event.preventDefault();

				const id = element.getAttribute(Header.attributes.linkId);

				if (!id) return;

				if (this.isOverlayClosed() && !this.hasOpenSubLinks(element)) {
					this.updateNavigation([id]);
					this.setSelectedLinkImage(id);
					this.showNavigation();
					this.openOverlay();
				} else if (this.isOverlayOpen() && this.hasOpenSubLinks(element)) {
					this.closeOverlay();
				} else {
					this.updateNavigation([id]);
					this.showNavigation();
					this.setSelectedLinkImage(id);
				}

				this.hideSearch();
			});
		});

		[...overlayLinksWithSubLinks, ...overlayMetaLinksWithSubLinks].forEach((element): void => {
			const navigationPath = Header.extractNavigationPathIds(element);

			element.addEventListener('click', (event): void => {
				event.preventDefault();

				if (navigationPath) {
					this.updateNavigation(navigationPath);
					this.setSelectedLinkImage(navigationPath[navigationPath.length - 1]);
				}
			});
		});

		[...overlayLinks, ...overlayMetaLinks].forEach((element): void => {
			element.addEventListener('mouseenter', (): void => {
				const id = element.getAttribute(Header.attributes.linkId);
				const parent = element.closest(Header.domSelectors.overlayLinkGroup);

				if (!parent) return;
				const level = parent.getAttribute(Header.attributes.level);

				// only in last level navigation
				if (id && level && level === this.currentLevel()) {
					clearTimeout(this.imageVisibilityTimeout);
					this.showHoveredLinkImage(id);
					this.hideSelectedLinkImage();
				}
			});

			element.addEventListener('mouseleave', (): void => {
				this.imageVisibilityTimeout = setTimeout((): void => {
					this.hideHoveredLinkImage();
					this.showSelectedLinkImage();
				}, 600) as any;
			});
		});

		contextualLinks.forEach((element): void => {
			element.addEventListener('mouseenter', (): void => {
				const linkContext = element.dataset.linkContext as string;
				this.element.setAttribute(Header.attributes.hoveredLinkContext, linkContext);
			});

			element.addEventListener('mouseleave', (): void => {
				this.element.removeAttribute(Header.attributes.hoveredLinkContext);
			});
		});

		// just for mobile
		if (backButton) {
			backButton.addEventListener('click', (): void => {
				const openLinkGroups: HTMLElement[] = Array.from(
					this.element.querySelectorAll(`.${Header.stateClasses.isOpenLinkGroup}`)
				);

				const overlayLinksWrapper: HTMLElement | null = this.element.querySelector(
					Header.domSelectors.overlayLinksWrapper
				);
				const currentOpenLinkGroup = openLinkGroups[openLinkGroups.length - 1];
				const parentIds = currentOpenLinkGroup.getAttribute(Header.attributes.parentIds);
				const parentIdsArray = parentIds ? Header.removeRootParentId(parentIds.split(',')) : [];

				if (overlayLinksWrapper) {
					if (parentIdsArray.length === 1) {
						this.transitionEnd(
							overlayLinksWrapper,
							(): void => this.element.setAttribute(Header.attributes.currentLevel, '1'),
							'transform'
						).then((): void => {
							this.resetNavigation();
						});
					} else {
						this.transitionEnd(
							overlayLinksWrapper,
							(): void => this.element.setAttribute(Header.attributes.currentLevel, parentIdsArray.length.toString()),
							'transform'
						).then((): void => {
							parentIdsArray.pop();
							this.updateNavigation(parentIdsArray);
						});
					}
				}
			});
		}

		window.addEventListener('scroll', (): void => this.throttledScrollEvent());

		this.initMqManager();
		this.checkPageScroll();
	}

	public moveNavigationBarOut(): void {
		this.element.classList.add(Header.stateClasses.hasHiddenNavigationBar);
	}

	public moveNavigationBarIn(): void {
		this.element.classList.remove(Header.stateClasses.hasHiddenNavigationBar);
	}

	protected checkPageScroll(): void {
		if (this.ignoreScrollEvent) {
			this.downScrollSnapshot = null;
			this.upScrollSnapshot = null;
			return;
		}

		const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
		const isScrollingDown = scrollPosition > this.lastScrollPosition;
		const isScrollingUp = scrollPosition < this.lastScrollPosition;
		const downScrollThreshold = 200;
		const upScrollThreshold = 150;

		if (isScrollingDown) {
			if (!this.downScrollSnapshot) {
				this.downScrollSnapshot = scrollPosition;
				this.upScrollSnapshot = null;
			}

			if (scrollPosition - this.downScrollSnapshot > downScrollThreshold) {
				this.moveNavigationBarOut();
			}
		} else if (isScrollingUp) {
			if (!this.upScrollSnapshot) {
				this.upScrollSnapshot = scrollPosition;
				this.downScrollSnapshot = null;
			}

			if (this.upScrollSnapshot - scrollPosition > upScrollThreshold || scrollPosition < upScrollThreshold) {
				this.moveNavigationBarIn();
			}
		}
		this.lastScrollPosition = scrollPosition;
	}

	private initMqManager(): void {
		const mqLarge = window.matchMedia('(min-width: 981px)');

		const checkMql = (): void => {
			// close menu if we resize the viewport from a smaller viewport to large as it can result in invalid state
			if (mqLarge.matches && this.isOverlayOpen() && this.isNavigationVisible() && this.currentLevel() === '1') {
				this.closeOverlay();
			}
		};

		if (mqLarge.addEventListener) {
			mqLarge.addEventListener('change', checkMql);
		} else {
			// Safari only supports the old syntax
			mqLarge.addListener(checkMql);
		}
	}

	private openOverlay(): void {
		LayoutUtil.deactivateMainContainer();
		this.element.classList.add(Header.stateClasses.hasDisplayedOverlay);
		setTimeout((): void => {
			this.element.classList.add(Header.stateClasses.hasOpenOverlay);
			focusLock.on(this.element);
		}, 20);
	}

	private closeOverlay(): void {
		const overlay: HTMLElement | null = this.element.querySelector(Header.domSelectors.overlay);

		if (!overlay) return;

		this.transitionEnd(
			overlay,
			(): void => this.element.classList.remove(Header.stateClasses.hasOpenOverlay),
			'visibility'
		).then((): void => {
			this.resetNavigation();
			this.hideSearch();
			this.hideNavigation();
			LayoutUtil.activateMainContainer();
			focusLock.off(this.element);
			this.element.classList.remove(Header.stateClasses.hasDisplayedOverlay);
		});
	}

	protected getLinkDomElements(): Record<string, HTMLElement[]> {
		return {
			topMainLinks: Array.from(this.element.querySelectorAll(Header.domSelectors.topMainLink)),
			metaLinks: Array.from(this.element.querySelectorAll(Header.domSelectors.metaLink)),
			overlayLinks: Array.from(this.element.querySelectorAll(Header.domSelectors.overlayLink)),
			linkGroups: Array.from(this.element.querySelectorAll(Header.domSelectors.overlayLinkGroup))
		};
	}

	private updateNavigation(navigationPath: string[]): void {
		const domElements = this.getLinkDomElements();

		this.resetNavigation();

		[...domElements.topMainLinks, ...domElements.metaLinks, ...domElements.overlayLinks].forEach((element): void => {
			const id = element.getAttribute(Header.attributes.linkId);

			if (id && navigationPath.includes(id)) {
				element.classList.add(Header.stateClasses.hasOpenLinkGroup);
			}
		});

		domElements.linkGroups.forEach((element): void => {
			const parentIds = element.getAttribute(Header.attributes.parentIds);
			const parentIdsArray = parentIds ? Header.removeRootParentId(parentIds.split(',')) : [];

			navigationPath.forEach((id, index): void => {
				if (parentIdsArray.includes(id) && parentIdsArray.length <= index + 1) {
					element.classList.add(Header.stateClasses.isOpenLinkGroup);
					if (parentIdsArray[parentIdsArray.length - 1] === navigationPath[navigationPath.length - 1]) {
						element.classList.add(Header.stateClasses.isCurrentLinkGroup);
					}
				}
			});
		});
		this.element.setAttribute(Header.attributes.currentLevel, (navigationPath.length + 1).toString());
	}

	private resetNavigation(): void {
		const domElements = this.getLinkDomElements();

		[...domElements.topMainLinks, ...domElements.metaLinks, ...domElements.overlayLinks].forEach((element): void =>
			element.classList.remove(Header.stateClasses.hasOpenLinkGroup)
		);

		domElements.linkGroups.forEach((element): void => {
			element.classList.remove(Header.stateClasses.isOpenLinkGroup);
			element.classList.remove(Header.stateClasses.isCurrentLinkGroup);
		});

		this.resetImages();

		this.element.setAttribute(Header.attributes.currentLevel, '1');
	}

	private showHoveredLinkImage(id: string): void {
		const currentHoveredLinkImage = this.getHoveredLinkImage();
		const newImage = this.element.querySelector(`[${Header.attributes.imageId}="${id}"]`);

		if (currentHoveredLinkImage) {
			currentHoveredLinkImage.classList.remove(Header.stateClasses.hasHoveredParentLink);
		}

		if (newImage) {
			newImage.classList.add(Header.stateClasses.hasHoveredParentLink);
		}
	}

	private setSelectedLinkImage(id: string): void {
		const currentSelectedLinkImage = this.getSelectedLinkImage();
		const newImage = this.element.querySelector(`[${Header.attributes.imageId}="${id}"]`);

		if (currentSelectedLinkImage) {
			currentSelectedLinkImage.classList.remove(Header.stateClasses.isSelectedLinkImage);
		}

		if (newImage) {
			newImage.classList.add(Header.stateClasses.isSelectedLinkImage);
		}
	}

	private getSelectedLinkImage(): HTMLElement | null {
		return this.element.querySelector(`.${Header.stateClasses.isSelectedLinkImage}`);
	}

	private hideSelectedLinkImage(): void {
		const currentSelectedLinkImage = this.getSelectedLinkImage();
		if (currentSelectedLinkImage) {
			currentSelectedLinkImage.classList.add(Header.stateClasses.isHidden);
		}
	}

	private showSelectedLinkImage(): void {
		const currentSelectedLinkImage = this.getSelectedLinkImage();
		if (currentSelectedLinkImage) {
			currentSelectedLinkImage.classList.remove(Header.stateClasses.isHidden);
		}
	}

	private hideHoveredLinkImage(): void {
		const images: HTMLElement[] = Array.from(this.element.querySelectorAll(Header.domSelectors.linkImage));
		images.forEach((image): void => {
			image.classList.remove(Header.stateClasses.hasHoveredParentLink);
		});
	}

	private resetImages(): void {
		const images: HTMLElement[] = Array.from(this.element.querySelectorAll(Header.domSelectors.linkImage));
		images.forEach((image): void => {
			image.classList.remove(Header.stateClasses.hasHoveredParentLink);
			image.classList.remove(Header.stateClasses.isSelectedLinkImage);
			image.classList.remove(Header.stateClasses.isHidden);
		});
	}

	private getHoveredLinkImage(): HTMLElement | null {
		return this.element.querySelector(`.${Header.stateClasses.hasHoveredParentLink}`);
	}

	private showNavigation(): void {
		this.element.classList.add(Header.stateClasses.hasVisibleNavigation);
	}

	private hideNavigation(): void {
		this.element.classList.remove(Header.stateClasses.hasVisibleNavigation);
	}

	private showSearch(): void {
		if (!this.isSearchInitialized) {
			const searchContainer: HTMLElement = this.element.querySelector(
				Header.domSelectors.searchContainer
			) as HTMLElement;

			const search: HTMLElement = document.createElement('div');
			search.setAttribute('data-init', 'M024');
			search.setAttribute('data-component-id', 'M024');
			searchContainer.appendChild(search);

			document.dispatchEvent(new CustomEvent('DOMContentLoaded'));

			this.isSearchInitialized = true;
		} else {
			window.setTimeout((): void => {
				const searchInput: HTMLElement | null = this.element.querySelector(Header.domSelectors.searchInput);
				if (searchInput) searchInput.focus();
			}, 200);
		}

		this.element.classList.add(Header.stateClasses.hasVisibleSearch);
	}

	private hideSearch(): void {
		this.element.classList.remove(Header.stateClasses.hasVisibleSearch);
	}

	private isOverlayOpen(): boolean {
		return this.element.classList.contains(Header.stateClasses.hasOpenOverlay);
	}

	private isOverlayClosed(): boolean {
		return !this.element.classList.contains(Header.stateClasses.hasOpenOverlay);
	}

	private isNavigationVisible(): boolean {
		return this.element.classList.contains(Header.stateClasses.hasVisibleNavigation);
	}

	private isSearchHidden(): boolean {
		return !this.element.classList.contains(Header.stateClasses.hasVisibleSearch);
	}

	private currentLevel(): string {
		return this.element.getAttribute(Header.attributes.currentLevel) as string;
	}

	private hasOpenSubLinks(element: HTMLElement): boolean {
		return element.classList.contains(Header.stateClasses.hasOpenLinkGroup);
	}

	private transitionEnd(
		elementToAnimate: HTMLElement,
		animationAction: () => void,
		propertyToCheck: string
	): Promise<boolean> {
		return new Promise((resolve): void => {
			animationAction();
			const transitionEnded = (event: TransitionEvent): void => {
				if (event.propertyName !== propertyToCheck) return;
				elementToAnimate.removeEventListener('transitionend', transitionEnded);
				resolve();
			};
			elementToAnimate.addEventListener('transitionend', transitionEnded);
		});
	}

	private static extractNavigationPathIds(element: HTMLElement): string[] | undefined {
		const id = element.getAttribute('data-link-id');
		const parentElement = element.closest(`[${Header.attributes.level}]`);

		if (!parentElement || !id) return;

		const parentIds: string | null = parentElement.getAttribute(Header.attributes.parentIds);
		const parentIdsArray = parentIds ? Header.removeRootParentId([...parentIds.split(',')]) : [];

		return [...parentIdsArray, id];
	}

	public static isOverlayOpen(): boolean {
		const header = document.querySelector(Header.domSelectors.mainWrapper);

		if (header) {
			return header.classList.contains(Header.stateClasses.hasOpenOverlay);
		}
		return false;
	}

	private static removeRootParentId(arr: string[]): string[] {
		const index = arr.indexOf('main-null');
		if (index > -1) {
			arr.splice(index, 1);
		}
		return arr;
	}
}
